import { Cross1Icon, FileIcon } from '@radix-ui/react-icons'
import {
  Node,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  mergeAttributes,
} from '@tiptap/react'
import { clsx } from 'clsx'
import { UploadFilesPlugin } from '~/lib/tiptap/plugins/upload-file'
import { IconButton } from '~/sotano-kit/primitives/icon-button'

const fileNodeName = 'file'

export const FileNode = Node.create({
  name: fileNodeName,
  group: 'block', // 'inline' or 'block' based on your need
  atom: true, // prevents nested content, common for widgets
  selectable: true,
  draggable: true, // important for consistent dragging behavior
  isolating: true, // prevents weird nesting/collapsing behaviors
  inline: false,
  addNodeView() {
    return ReactNodeViewRenderer(FileNodeView)
  },
  addAttributes() {
    return {
      src: {
        default: '',
      },
      name: {
        default: '',
      },
      mimeType: {
        default: '',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div[data-type="${fileNodeName}"]`,
        getAttrs: (dom) => {
          return {
            src: dom.getAttribute('data-file-src'),
            name: dom.getAttribute('data-file-name'),
            mimeType: dom.getAttribute('data-file-mime-type'),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': fileNodeName }),
      0,
    ]
  },

  addPasteRules() {
    // optional: clean up pasted HTML
    return []
  },
  addProseMirrorPlugins() {
    return [UploadFilesPlugin()]
  },
})

const FileNodeView: React.FC<NodeViewProps> = ({
  node,
  editor,
  deleteNode,
  selected,
}) => {
  const handleFileClick = () => {
    if (node.attrs.src) {
      window.open(node.attrs.src, '_blank')
    }
  }

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    deleteNode()
  }
  return (
    <NodeViewWrapper>
      <div
        contentEditable={false}
        className={clsx(
          'flex items-center gap-2 bg-transparent border border-dark-border-solid rounded px-2 py-1 cursor-pointer hover:bg-dark-subtle transition-colors max-w-full mb-2',
          editor.isEditable &&
            editor.isFocused &&
            selected &&
            'outline outline-2 outline-dark-accent'
        )}
        data-file-name={node.attrs.name}
        data-file-src={node.attrs.src}
        data-file-mime-type={node.attrs.mimeType}
        onClick={handleFileClick}
        title={`Click to open ${node.attrs.name}`}
      >
        <FileIcon className="w-4 h-4 text-dark-muted shrink-0" />
        <span className="text-sm text-dark-bright truncate w-full shrink-1">
          {node.attrs.name}
        </span>
        {editor.isEditable && (
          <IconButton
            intent="secondary"
            size="xs"
            className="ml-auto shrink-0 !bg-transparent"
            onClick={handleRemoveFile}
          >
            <Cross1Icon className="w-4 h-4 text-dark-muted" />
          </IconButton>
        )}
      </div>
    </NodeViewWrapper>
  )
}
